import { useEffect, useState } from 'react'
import { Button, Dialog } from '../../components/ui'
import type { TenantUser } from './useTenantUsers'

const field = 'h-[38px] w-full rounded-lg border border-line-strong bg-surface px-3 text-sm text-ink focus:outline-none focus-visible:ring-[3px] focus-visible:ring-focus'

export function AssignOwnerDialog({ open, count, users, onClose, onAssign }: {
  open: boolean
  count: number
  users: TenantUser[]
  onClose: () => void
  onAssign: (ownerUserId: string | null) => Promise<void>
}) {
  const [owner, setOwner] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => { if (open) setOwner('') }, [open])

  async function save(value: string | null) {
    setSaving(true)
    try {
      await onAssign(value)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Assign owner"
      description={`The owner is responsible for remediating ${count === 1 ? 'this person' : `these ${count} people`}.`}
      footer={
        <>
          <Button variant="ghost" onClick={() => save(null)} disabled={saving}>Unassign</Button>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={() => save(owner)} disabled={!owner} loading={saving}>Assign</Button>
        </>
      }
    >
      <label htmlFor="dw-owner" className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Owner</label>
      <select id="dw-owner" value={owner} onChange={e => setOwner(e.target.value)} className={field}>
        <option value="">Choose a user…</option>
        {users.map(u => <option key={u.id} value={u.id}>{u.username} ({u.email})</option>)}
      </select>
    </Dialog>
  )
}

export function CreateTaskDialog({ open, emails, users, onClose, onCreate }: {
  open: boolean
  emails: string[]
  users: TenantUser[]
  onClose: () => void
  onCreate: (input: { assignedTo: string | null; dueDate: number | null; priority: string; note: string }) => Promise<void>
}) {
  const [assignedTo, setAssignedTo] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [priority, setPriority] = useState('high')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  useEffect(() => {
    if (open) {
      setAssignedTo('')
      setPriority('high')
      setNote('')
      // Default: two days out, matching "all critical within 48 hours"
      setDueDate(new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10))
    }
  }, [open])

  async function create() {
    setSaving(true)
    try {
      await onCreate({
        assignedTo: assignedTo || null,
        dueDate: dueDate ? new Date(`${dueDate}T17:00:00`).getTime() : null,
        priority,
        note,
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Create remediation task"
      description={`One task covering ${emails.length === 1 ? emails[0] : `${emails.length} people`}. It lists the steps: reset passwords, revoke sessions, and check devices for malware.`}
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" onClick={create} loading={saving}>Create task</Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="dw-task-assignee" className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Assignee</label>
          <select id="dw-task-assignee" value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className={field}>
            <option value="">Unassigned</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.username}</option>)}
          </select>
        </div>
        <div>
          <label htmlFor="dw-task-priority" className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Priority</label>
          <select id="dw-task-priority" value={priority} onChange={e => setPriority(e.target.value)} className={field}>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
        <div>
          <label htmlFor="dw-task-due" className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Due date</label>
          <input id="dw-task-due" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} className={field} />
        </div>
        <div className="sm:col-span-2">
          <label htmlFor="dw-task-note" className="mb-1.5 block text-[13px] font-medium text-ink-secondary">Note (optional)</label>
          <textarea id="dw-task-note" value={note} onChange={e => setNote(e.target.value)} maxLength={1000} rows={3} className={`${field} h-auto py-2`} placeholder="e.g. ticket number or context" />
        </div>
      </div>
    </Dialog>
  )
}
